import type { Message } from "@apis:message/message";
import * as Sentry from "@sentry/react";
import hljs from "highlight.js";
import { get } from "lodash";

import { CODE_LANGUAGE_MAP } from "./constants";

export type ReferenceDoc = Message["GET_ONE"]["Response"]["docs"][number];

// Highlight.js relevance threshold for confident language detection
const LANGUAGE_DETECTION_CONFIDENCE_THRESHOLD = 5;

const logError = (
  functionName: string,
  error: unknown,
  context?: Record<string, unknown>,
) => {
  console.error(`[MarkdownUtils.${functionName}]`, error, context);

  Sentry.captureException(error, {
    tags: { function: functionName },
    extra: context,
  });
};

export const sanitizeMarkdown = (text: string): string => {
  try {
    // Remove script tags and their content
    let sanitized = text.replace(
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      "",
    );

    // Remove event handler attributes (onclick, onerror, etc.)
    sanitized = sanitized.replace(/on\w+\s*=\s*["'][^"']*["']/gi, "");

    // Remove javascript: protocol
    sanitized = sanitized.replace(/javascript:/gi, "");

    // Remove data: URLs for HTML content
    sanitized = sanitized.replace(/data:text\/html/gi, "");

    // Remove vbscript: protocol
    sanitized = sanitized.replace(/vbscript:/gi, "");

    return sanitized;
  } catch (error) {
    logError("sanitizeMarkdown", error, { textLength: text.length });
    return text; // Fallback to raw text on error
  }
};

export const createReferenceMap = (
  references: ReferenceDoc[],
): Record<number, ReferenceDoc> => {
  try {
    const map: Record<number, ReferenceDoc> = {};
    references.forEach((ref) => {
      // Use searchRanking as the key since the answer text uses [N] where N is searchRanking
      const ranking = ref.searchRanking;
      if (ranking !== null && ranking !== undefined) {
        map[ranking] = ref;
      }
    });
    return map;
  } catch (error) {
    logError("createReferenceMap", error, {
      referencesCount: references.length,
    });
    return {};
  }
};

export const preprocessMarkdownText = (
  text: string,
  references: ReferenceDoc[],
): string => {
  try {
    const referenceMap = createReferenceMap(references);

    // Convert [N] to markdown reference links
    const processed = text.replace(/\[(\d+)\]/g, (match, numStr) => {
      const num = parseInt(numStr, 10);
      const ref = referenceMap[num];

      // If reference doesn't exist, keep original [N]
      if (!ref) {
        return match;
      }

      // Use reference field as the link target/tooltip
      const refText = get(ref, "reference", "") as string;

      // Return markdown link: [N](#ref-N "tooltip")
      // The #ref-N will be intercepted by custom link component
      return `[${num}](#ref-${num} "${refText}")`;
    });

    // Sanitize after processing to prevent XSS
    return sanitizeMarkdown(processed);
  } catch (error) {
    logError("preprocessMarkdownText", error, {
      textLength: text.length,
      referencesCount: references.length,
    });
    return text; // Fallback to raw text on error
  }
};

export const extractLanguage = (className?: string, code?: string): string => {
  try {
    // Try to extract from className first
    if (className) {
      const match = className.match(/language-(\w+)/);
      if (match) {
        const lang = match[1];
        // Map alias to canonical name
        return CODE_LANGUAGE_MAP[lang] || lang;
      }
    }

    // Auto-detect language if code provided
    if (code && code.trim().length > 0) {
      try {
        const result = hljs.highlightAuto(code);
        if (
          result.language &&
          result.relevance > LANGUAGE_DETECTION_CONFIDENCE_THRESHOLD
        ) {
          return result.language;
        }
      } catch (detectError) {
        logError("extractLanguage.autoDetect", detectError, {
          codeLength: code.length,
        });
      }
    }

    return "plaintext";
  } catch (error) {
    logError("extractLanguage", error, { className, codeLength: code?.length });
    return "plaintext";
  }
};

export const extractCodeString = (children: React.ReactNode): string => {
  try {
    if (typeof children === "string") {
      return children;
    }

    if (Array.isArray(children)) {
      return children
        .map((child) => (typeof child === "string" ? child : String(child)))
        .join("");
    }

    return String(children || "");
  } catch (error) {
    logError("extractCodeString", error);
    return "";
  }
};
